import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

const AGREEMENT_VERSION = '1.0';

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const MyStoreContract = ({ refreshUserData, onSignOut }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [loading, setLoading] = useState(false);

  const canSubmit = agreed && signatureName.trim();

  const handleSubmit = async () => {
    if (!canSubmit || loading) return;
    setLoading(true);

    try {
      const { error } = await supabase
        .from('users')
        .update({
          contract_agreed: true,
          contract_agreed_at: new Date().toISOString(),
        })
        .eq('auth_id', user.id);

      if (error) throw error;
      refreshUserData();
    } catch (err) {
      console.error('Contract error:', err);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    card: {
      width: '100%',
      maxWidth: 480,
    },
    header: {
      marginBottom: 20,
    },
    stepLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.accent,
      textTransform: 'uppercase',
      letterSpacing: 1,
      margin: '0 0 6px',
    },
    title: {
      fontSize: 22,
      fontWeight: 700,
      color: theme.text,
      margin: '0 0 6px',
    },
    subtitle: {
      fontSize: 14,
      color: theme.textSecondary,
      margin: 0,
    },
    agreementBox: {
      maxHeight: 300,
      overflowY: 'auto',
      padding: 16,
      backgroundColor: theme.bgCard,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      marginBottom: 16,
      fontSize: 13,
      color: theme.textSecondary,
      lineHeight: 1.6,
    },
    agreementTitle: {
      fontWeight: 600,
      marginTop: 0,
      color: theme.text,
    },
    viewFullLink: {
      color: theme.accent,
      textDecoration: 'underline',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      fontSize: 13,
      padding: 0,
      marginBottom: 20,
      display: 'inline-block',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      marginBottom: 20,
      cursor: 'pointer',
    },
    checkbox: {
      width: 22,
      height: 22,
      borderRadius: 6,
      border: `2px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 1,
    },
    checkboxChecked: {
      backgroundColor: theme.accent,
      border: `2px solid ${theme.accent}`,
    },
    checkboxLabel: {
      fontSize: 14,
      color: theme.text,
      lineHeight: 1.4,
    },
    fieldGroup: {
      marginBottom: 20,
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: theme.text,
      marginBottom: 6,
    },
    required: {
      color: '#ef4444',
    },
    input: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 17,
      fontStyle: 'italic',
      boxSizing: 'border-box',
      outline: 'none',
    },
    button: {
      width: '100%',
      padding: 14,
      borderRadius: 12,
      border: 'none',
      backgroundColor: theme.accent,
      color: '#fff',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    signOutBtn: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: 'transparent',
      color: theme.textSecondary,
      fontSize: 14,
      cursor: 'pointer',
      marginTop: 12,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <p style={styles.stepLabel}>Step 3 of 3</p>
          <h2 style={styles.title}>Partner Agreement</h2>
          <p style={styles.subtitle}>Review and sign the agreement to activate your location.</p>
        </div>

        <div style={styles.agreementBox}>
          <p style={styles.agreementTitle}>
            CardChase Location Partner Agreement (v{AGREEMENT_VERSION})
          </p>
          <p>
            By signing this agreement, you confirm that you are authorized to represent this business location on the CardChase platform.
          </p>
          <p><strong>You agree to:</strong></p>
          <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
            <li>Provide accurate and current business information</li>
            <li>Keep operating hours and event schedules up to date</li>
            <li>Maintain a safe and welcoming environment for CardChase users</li>
            <li>Not misrepresent your business or affiliation with CardChase</li>
            <li>Comply with all applicable laws</li>
          </ul>
          <p><strong>You acknowledge that:</strong></p>
          <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
            <li>CardChase is a listing platform, not an endorser or business partner</li>
            <li>CardChase is not liable for any incidents at your location</li>
            <li>Your verified status can be revoked for violation of these terms</li>
            <li>Ownership disputes will be reviewed and decided by the CardChase team</li>
          </ul>
          <p>
            CardChase may update this agreement. Continued participation constitutes acceptance of updated terms.
          </p>
        </div>

        <button
          style={styles.viewFullLink}
          onClick={() => navigate('/legal/location-partner-agreement')}
        >
          View Full Agreement
        </button>

        <div style={styles.checkboxRow} onClick={() => setAgreed(!agreed)}>
          <div style={{
            ...styles.checkbox,
            ...(agreed ? styles.checkboxChecked : {}),
          }}>
            {agreed && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
          <span style={styles.checkboxLabel}>
            I have read and agree to the Location Partner Agreement
          </span>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>
            Type your full name as your digital signature <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            placeholder="Full legal name"
            value={signatureName}
            onChange={(e) => setSignatureName(e.target.value)}
            style={styles.input}
          />
        </div>

        <button
          style={{
            ...styles.button,
            ...(!canSubmit || loading ? styles.buttonDisabled : {}),
          }}
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
        >
          <ShieldIcon />
          {loading ? 'Signing...' : 'Sign Agreement'}
        </button>

        <button style={styles.signOutBtn} onClick={onSignOut}>
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default MyStoreContract;
